import { Suspense, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Navigate, useParams } from "react-router";
import { ErrorBox, NotFound, useNoIndex, useTitle } from "../components/Layout.js";
import { Drawing, Sketch } from "../components/lazy.js";
import { PairSection } from "../components/Pairs.js";
import { TextsProvider } from "../components/texts.js";
import { Tone } from "../components/tone.js";
import { typeName } from "../components/type-name.js";
import { ApiError, api, type FeedbackValue, type LinkedResult } from "../data/api.js";

// Skica přes identitu `/{jazyk}/me/results/{id}` (REQ-033): majitel na kterémkoli přihlášeném
// zařízení, „Sedí to na mě?“ přes identitu. Odkaz ke sdílení vydává Moje já (nový odkaz).

export function MeResultPage() {
  const intl = useIntl();
  const lang = intl.locale;
  const { id = "" } = useParams();
  const [data, setData] = useState<LinkedResult | null>(null);
  const [status, setStatus] = useState<"loading" | "ok" | "missing" | "signed-out" | "error">("loading");
  const [feedback, setFeedback] = useState<Record<string, FeedbackValue>>({});
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  useNoIndex();
  useTitle(data === null ? undefined : typeName(data.texts, data.payload.type, data.locale));

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const r = await api.identityResult(id, lang);
      setData(r);
      setFeedback(Object.fromEntries(r.feedback.filter((f) => f.scale_id !== null && f.section_key === null).map((f) => [f.scale_id, f.value])));
      // Vygenerovat odkaz pro sdílení (Libor 3. 11.: sdílení přímo na výsledku, ne jen z Moje já).
      api.identityLink(id).then((link) => setShareUrl(`${window.location.origin}/${lang}/r/${link.result_token}`)).catch(() => undefined);
      setStatus("ok");
    } catch (e) {
      setStatus(e instanceof ApiError && e.status === 401 ? "signed-out" : e instanceof ApiError && e.status === 404 ? "missing" : "error");
    }
  }, [id, lang]);

  useEffect(() => {
    void load();
  }, [load]);

  if (status === "signed-out") return <Navigate to={`/${lang}/me`} replace />;
  if (status === "missing") return <NotFound />;
  if (status === "error") return <ErrorBox onRetry={() => void load()} />;
  if (data === null) return null;
  return (
    <TextsProvider texts={data.texts} locale={data.locale} preview={false}>
      <Tone slug={data.view.slug}>
        <Suspense fallback={<Drawing />}>
          <Sketch
            payload={data.payload}
            view={data.view}
            sessionId={data.session_id ?? null}
            pictureUrl={data.picture_url ?? null}
            portrait={data.portrait === undefined ? null : { offer: data.portrait, owner: { kind: "identity", resultId: id }, page: `/${lang}/me/results/${id}/portrait` }}
            portraitSource={{ kind: "identity", resultId: id }}
            pairs={data.session_id === undefined ? null : <PairSection owner={{ kind: "identity", sessionId: data.session_id }} />}
            owner
            shareUrl={shareUrl}
            revoked={false}
            onRevoke={null}
            feedback={(d) => ({
              value: feedback[d] ?? null,
              onChange: (v) => {
                setFeedback((f) => ({ ...f, [d]: v }));
                void api.identityFeedback(id, { scale_id: d, section_key: null, value: v }).catch(() => setFeedback((f) => ({ ...f, [d]: feedback[d] ?? v })));
              },
            })}
          />
        </Suspense>
      </Tone>
    </TextsProvider>
  );
}
